// src/components/paxTypes/FormAddEditPaxTypes.jsx

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Grid, Stack, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import useCreatePaxType from "../../hooks/paxTypes/useCreatePaxType";
import useUpdatePaxType from "../../hooks/paxTypes/useUpdatePaxType";
import FormInput from "../inputs/FormInput";
import i18next from "i18next";
import LanguageToggle from "../global/LanguageToggle";

const FormAddEditPaxTypes = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [nameAr, setNameAr] = useState(currentData?.name_ar || "");
    const [nameEn, setNameEn] = useState(currentData?.name_en || "");
    const [status, setStatus] = useState(currentData?.status || "active");

    const { createPaxTypes, isPending: isCreating } = useCreatePaxType();
    const { updatePaxTypes, isPending: isUpdate } = useUpdatePaxType();

    const loadingSubmit = isCreating || isUpdate;

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setStatus(currentData?.status || "active");
        }
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();
        const payload = {
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            status,
        };
        if (isEdit && currentData?.id) {
            updatePaxTypes(
                { id: currentData.id, req: payload },
                {
                    onSuccess: () => {
                        if (onSubmitSuccess) onSubmitSuccess();
                    },
                }
            );
        } else {
            createPaxTypes(payload, {
                onSuccess: () => {
                    if (onSubmitSuccess) onSubmitSuccess();
                },
            });
        }
    };

    // Status options for FormInput select
    const statusOptions = [
        { id: "active", name: isArabic ? "نشط" : "Active" },
        { id: "disabled", name: isArabic ? "معطل" : "Disabled" },
    ];

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box
                component="form"
                onSubmit={SubmitForm}
                sx={{
                    p: { xs: 2, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Typography variant="h6">
                        {isEdit
                            ? isArabic
                                ? "تعديل نوع ركاب"
                                : "Edit Pax Type"
                            : isArabic
                            ? "إضافة نوع ركاب جديد"
                            : "Create New Pax Type"}
                    </Typography>

                    <LanguageToggle
                        activeLang={activeLang}
                        onChange={(lng) => setActiveLang(lng)}
                        showLabel={true}
                    />
                </Box>

                <Box mb={3}>
                    <Grid container spacing={2}>
                        {showArabic && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="الاسم (عربي)"
                                    placeholder="أدخل اسم نوع الركاب بالعربي"
                                    type="text"
                                    name="name_ar"
                                    value={nameAr}
                                    onChange={(e) => setNameAr(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        {showEnglish && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Name (English)"
                                    placeholder="Enter pax type name in English"
                                    type="text"
                                    name="name_en"
                                    value={nameEn}
                                    onChange={(e) => setNameEn(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "الحالة" : "Status"}
                                type="select"
                                name="status"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                options={statusOptions}
                                required
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Stack direction="row" spacing={1}>
                        <Button 
                                variant="outlined" 
                                color="inherit"
                                onClick={() => navigate(-1)}
                            >
                                {isArabic ? "تراجع" : "Cancel"}
                            </Button>
                            <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loadingSubmit}
                        >
                            {loadingSubmit
                                ? isArabic
                                    ? "جارٍ الحفظ..."
                                    : "Saving..."
                                : isEdit
                                ? isArabic
                                    ? "تحديث"
                                    : "Update"
                                : isArabic
                                ? "حفظ"
                                : "Save"}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditPaxTypes;